import { memo, useCallback, useMemo } from 'react';

import { type ShortUrlResponse } from '@air/api/types';

import { InfoCommentsSection } from '~/components/ShareModal/components/InfoCommentsSection/InfoCommentsSection';
import { OnOffShareToggleRow } from '~/components/ShareModal/components/OnOffShareToggleRow';
import { SecurityPrivacySection } from '~/components/ShareModal/components/SecurityPrivacySection/SecurityPrivacySection';
import { ShareBrandingAirLogoSettingRow } from '~/components/ShareModal/components/ShareBrandingAirLogoSettingRow';
import { ShareModalSectionLabel } from '~/components/ShareModal/components/ShareModalSectionLabel';
import { UpsellFlashIconButton } from '~/components/ShareModal/components/UpsellFlashIconButton';
import { VersionsAndDownloadingSection } from '~/components/ShareModal/components/VersionsAndDownloadingSection/VersionsAndDownloadingSection';
import { VisibilityPermissionsSection } from '~/components/ShareModal/components/VisibilityPermissionsSection/VisibilityPermissionsSection';
import { useUpdateShareBoardLink } from '~/components/SharePrivateBoardModal/hooks/useUpdateShareBoardLink';
import { SHARE_MODAL_UPSELL_MESSAGE } from '~/constants/upsells';
import { useShareSettings } from '~/hooks/useShareSettings';
import { shareUrlUpdatedLinkAccess } from '~/utils/shortUrl/shareUrlUpdatedAnalytics';

export type SharePrivateBoardModalBodyProps = {
  boardId: string;
  canEdit: boolean;
  canViewCustomFields: boolean;
  canEditCustomFields: boolean;
  canViewDiscussions: boolean;
  canManageDiscussions: boolean;
  shortIdInfo: ShortUrlResponse;
  workspaceCanDownload: boolean;
};

export const SharePrivateBoardModalBody = memo(
  ({
    boardId,
    canEdit,
    canViewCustomFields,
    canEditCustomFields,
    canViewDiscussions,
    canManageDiscussions,
    shortIdInfo,
    workspaceCanDownload,
  }: SharePrivateBoardModalBodyProps) => {
    const { onShortUrlUpdate, isLoading } = useUpdateShareBoardLink({ boardId, shortIdInfo });
    const { removeAirLogoEnabled } = useShareSettings();

    const handleToggleEnablePublicLink = useCallback(
      (enabled: boolean) =>
        onShortUrlUpdate({ props: { disabled: !enabled }, analytics: shareUrlUpdatedLinkAccess(enabled) }),
      [onShortUrlUpdate],
    );

    const airLogoUpsell = useMemo(
      () =>
        !removeAirLogoEnabled ? <UpsellFlashIconButton>{SHARE_MODAL_UPSELL_MESSAGE}</UpsellFlashIconButton> : undefined,
      [removeAirLogoEnabled],
    );

    return (
      <>
        <OnOffShareToggleRow
          label="Enable public link"
          disabled={!canEdit}
          checked={!shortIdInfo.disabled}
          isLoading={isLoading}
          onToggle={handleToggleEnablePublicLink}
        />
        <VersionsAndDownloadingSection
          isLoading={isLoading}
          onShortUrlUpdate={onShortUrlUpdate}
          shortIdInfo={shortIdInfo}
          workspaceCanDownload={workspaceCanDownload}
        />
        <InfoCommentsSection
          canEditCustomFields={canEditCustomFields}
          canManageDiscussions={canManageDiscussions}
          canViewCustomFields={canViewCustomFields}
          canViewDiscussions={canViewDiscussions}
          isLoading={isLoading}
          onShortUrlUpdate={onShortUrlUpdate}
          shortIdInfo={shortIdInfo}
        />
        <VisibilityPermissionsSection
          shortIdInfo={shortIdInfo}
          isLoading={isLoading}
          onShortUrlUpdate={onShortUrlUpdate}
        />
        <SecurityPrivacySection shortIdInfo={shortIdInfo} isLoading={isLoading} onShortUrlUpdate={onShortUrlUpdate} />
        <div className="flex flex-col gap-2">
          <ShareModalSectionLabel title="Branding" />
          <ShareBrandingAirLogoSettingRow
            shortIdInfo={shortIdInfo}
            onShortUrlUpdate={onShortUrlUpdate}
            disabled={!canEdit || !removeAirLogoEnabled}
            labelAccessory={airLogoUpsell}
          />
        </div>
      </>
    );
  },
);

SharePrivateBoardModalBody.displayName = 'SharePrivateBoardModalBody';
